import { createTheme, ThemeProvider } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from '@mui/icons-material/Info';
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {
  Alert,
  AppBar,
  Box,
  Button, Checkbox, CircularProgress,
  FormControl,
  IconButton,
  Paper,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
  Grid
} from "@mui/material";
import {
  DataGridPremium,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  useGridApiRef,
  useKeepGroupedColumnsHidden
} from "@mui/x-data-grid-premium";
import moment from "moment";
import { default as React, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import ColorConstants from "../../../defaults/ColorConstants";
import FieldNames from "../../../defaults/FieldNames.json";
import AuditLogService from "../../../services/AuditLogService";
import ErrorService from "../../../services/ErrorService";
import FeatureService from '../../../services/FeatureService';
import {
  calculateFiscalYearQuarter, getSaveableDate,
} from "../../../utils/DateHelper";
import { getStatusDirect, isUpdateFullySuccessful, isUpdatePartiallySuccessful } from '../../../utils/FeatureHelper';
import { commaFormatter, currencyFormatter } from "../../../utils/FormatHelper";
import { formatResponse, getURLPath } from "../../../utils/GridHelper";
import { queryAllSummaryService } from "../../../utils/SummaryHelper";
import fieldsDefinition from '../../details/FieldInfo.json';
import InFormAlert from "../../InFormAlert";
import InFormGenericModal from "../../InFormGenericModal";
import '../ApprovalStyles.css';
import WorkflowFiscalYearFilter from "../WorkflowFiscalYearFilter/WorkflowFiscalYearFilter";
import WorkflowGridFilters from "../WorkflowGridFilters/WorkflowGridFilters";
import { useSelector } from "react-redux";
import { doesFilterContainFundingOrgs } from "../../filter/customFilters/FilterHelper";
import { displayBulkApprovalFilterWarning } from "../../../utils/BulkApprovalHelper";
import { CurrentFilterExpressionPopup } from "../CurrentFilterExpressionPopup/CurrentFilterExpressionPopup";
import { setShowMap } from "../../../redux/slices/displayStateSlice";
import * as _ from 'lodash';
import { useDispatch } from "react-redux";

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  const finalWidth = width - 20;
  return {
    finalWidth,
    height
  };
}

// Style Variables
const theme = createTheme({
  overrides: {
    MuiSnackbar: {
      root: {
        top: '94px !important'
      },
    },
  },
});

const useStyles = makeStyles({
  underline: {
    "&&&:before": {
      borderBottom: "2px solid red",
    },
    "&&:after": {
      borderBottom: "2px solid red",
    },
  },
  condensedFormControl: {
    margin: "5px 15px 5px 15px",
    minWidth: 120,
    width: "90%",
  },
  formControl: {
    margin: "10px 15px 10px 15px",
    minWidth: 120,
    width: "90%",
  },
  formControlHeader: {
    margin: "0px 15px 0px 15px",
    minWidth: 120,
    width: "90%",
  },
  snackBarStyle: {
    top: "120px !important",
  },
});

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

export default function BulkApprovalDialog({
  userInfo,
  userRoleInfo,
  headerHeight,
}) {

  //----------------------
  // Page State Variables
  //----------------------

  // Core Variables
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { height, width } = useWindowDimensions();

  // Error Variables
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // External Expression
  const definitionExpression = useSelector((state) => state.filter.definitionExpression);
  const filterHasFiscalYear = definitionExpression.includes("FiscalYear");

  // Workflow and Fiscal Year Variables
  const [fiscalYearSelection, setFiscalYearSelection] = useState(null);
  const [fiscalYearOptions, setFiscalYearOptions] = useState([]);
  const [featureCounts,setFeatureCounts] = useState({
    TreatmentCount: 0,
    ActivityCount: 0,
    ActualTreatmentCount: 0,
    ActualActivityCount: 0
  });

  // Approval Data Grid Variables
  const [rowGroupingModel, setRowGroupingModel] = React.useState([
    'id',
    'Name',
  ]);
  const [gridColumns, setGridColumns] = useState([]);
  const [gridValues, setGridValues] = useState([]);
  const [isLocalApprover, setIsLocalApprover] = useState(false);
  const [isRegionApprover, setIsRegionApprover] = useState(false);
  const [isAgencyApprover, setIsAgencyApprover] = useState(false);
  const [isDepartmentApprover, setIsDepartmentApprover] = useState(false);
  const [isGridQueryLoading,setIsGridQueryLoading] = useState(false);
  const [isBulkApprovalLoading, setIsBulkApprovalLoading] = useState(false);
  const isGridLoading = isGridQueryLoading || isBulkApprovalLoading;
  const [fullDefinitionExpression, setFullDefinitionExpression] = useState("");
  const featureOutFields = [
    FieldNames.featureFields.fundingObjectID, // Note: Using the Funding Single Line View, this is the Funding row ObjectID
    FieldNames.featureFields.objectID, // Note: Attribute OBJECTID - Could be duplicated if there are multiple funding lines
    FieldNames.featureFields.id,
    FieldNames.featureFields.name,
    FieldNames.featureFields.class,
    FieldNames.featureFields.fundingDepartment,
    FieldNames.featureFields.fundingAgency,
    FieldNames.featureFields.fundingRegion,
    FieldNames.featureFields.fundingUnit,
    FieldNames.featureFields.fundingSource,
    FieldNames.featureFields.fundingSourceProgram,
    FieldNames.featureFields.fundingSourceCategory,
    FieldNames.featureFields.fundingSourceSubCategory,
    FieldNames.featureFields.initiationDate,
    FieldNames.featureFields.initiationFiscalYear,
    FieldNames.featureFields.completionDate,
    FieldNames.featureFields.completionFiscalYear,
    FieldNames.fundingFields.assetCost, 
    FieldNames.fundingFields.contractualCost, 
    FieldNames.fundingFields.otherCost, 
    FieldNames.fundingFields.grantsFixedCost, 
    FieldNames.fundingFields.personnelCost, 
    FieldNames.fundingFields.totalCost, 
    FieldNames.featureFields.calculatedAcresFundingSingleLineView,
    FieldNames.featureFields.localApprovalDate, 
    FieldNames.featureFields.regionalApprovalDate,
    FieldNames.featureFields.agencyApprovalDate,
    FieldNames.featureFields.departmentApprovalDate,
    FieldNames.featureFields.estimatedSuccessProbability,
    FieldNames.featureFields.feasibility,
    FieldNames.featureFields.priority,
    FieldNames.featureFields.isRTRL,
    FieldNames.featureFields.unApprovalDate,
    FieldNames.featureFields.joinID,
    FieldNames.featureFields.joinFeature,
    FieldNames.featureFields.entityType
  ];

  // Cost Variables
  const [bilCost,setBilCost] = useState(0);
  const [nonBilCost,setNonBilCost] = useState(0);
  const [contributedCost, setContributedCost] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [bilAcres,setBilAcres] = useState(0);
  const [nonBilAcres,setNonBilAcres] = useState(0);
  const [totalAcres, setTotalAcres] = useState(0);
  const [allBilCost,setAllBilCost] = useState(0);
  const [allNonBilCost,setAllNonBilCost] = useState(0);
  const [allContributedCost, setAllContributedCost] = useState(0);
  const [allTotalCost, setAllTotalCost] = useState(0);
  const [allBilAcres,setAllBilAcres] = useState(0);
  const [allNonBilAcres,setAllNonBilAcres] = useState(0);
  const [allTotalAcres, setAllTotalAcres] = useState(0);

  // Approval Action Variables
  const [selectionModel, setSelectionModel] = useState({ids: new Set(), type: 'include'});
  const [datePickerValue, setDatePickerValue] = useState(new Date());
  const [allSelectedItems, setAllSelectedItems] = useState([]);
  const [isLocalDateChecked, setIsLocalDateChecked] = useState(false);
  const [isRegionDateChecked, setIsRegionDateChecked] = useState(false);
  const [isAgencyDateChecked, setIsAgencyDateChecked] = useState(false);
  const [isDepartmentDateChecked, setIsDepartmentDateChecked] = useState(false);
  const [bulkApprovalErrors,setBulkApprovalErrors] = useState([]);
  const [showApproverWarningPopup,setShowApproverWarningPopup] = useState(false);
  const [approverWarningType,setApproverWarningType] = useState("");
  const [isApprovalDateConfirmationDialogOpen,setIsApprovalDateConfirmationDialogOpen] = useState(false);
  const [isApprovalDateInFuture,setIsApprovalDateInFuture] = useState(false);
  const [validSelectedItems,setValidSelectedItems] = useState([]);
  const [itemsAtTimeOfApproval,setItemsAtTimeOfApproval] = useState(0);
  const [selectedItemsLocalNotPriorRegional,setSelectedItemsLocalNotPriorRegional] = useState([]);
  const [selectedItemsRegionalNotAfterLocal,setSelectedItemsRegionalNotAfterLocal] = useState([]);
  const [selectedItemsRegionalNotPriorAgency,setSelectedItemsRegionalNotPriorAgency] = useState([]);
  const [selectedItemsAgencyNotAfterRegional,setSelectedItemsAgencyNotAfterRegional] = useState([]);
  const [selectedItemsAgencyNotPriorDepartment,setSelectedItemsAgencyNotPriorDepartment] = useState([]);
  const [selectedItemsDepartmentNotAfterAgency,setSelectedItemsDepartmentNotAfterAgency] = useState([]);
  const [selectedItemsCanUnapprove,setSelectedItemsCanUnapprove] = useState(false);
  const disableApprovalConfirm = isGridLoading;

  // ------------------
  //     Effect Hooks
  // ------------------

  // Initial Load Hooks

  useEffect(() => {
    dispatch(setShowMap(false));
  }, [dispatch]);

  useEffect(() => {
    let fyList = [];
    let pastFyList = [];
    let futureFyList = [];
    let currFY = calculateFiscalYearQuarter(new Date(), true);
    if (currFY) {
      for (let i = 1; i <= 3; i++) {
        pastFyList.unshift(currFY - i);
        futureFyList.push(currFY + i);
      }
    }

    fyList = [...pastFyList, currFY, ...futureFyList];
    fyList = fyList.map((year) => {
      return { FiscalYear: `${year}` };
    });
    setFiscalYearOptions(fyList);
    setFiscalYearSelection({ FiscalYear: `${currFY}` });
  }, []);

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      density: "compact",
      aggregation: {
        model: {
          [FieldNames.fundingFields.totalCost]: 'sum',
          [FieldNames.featureFields.calculatedAcresFundingSingleLineView]: 'sum',
        },
      },
      columns: {
        columnVisibilityModel: { 
          GroupingID: false,
          Name: false,
          checkboxSelection: false,
          group: false,
          CalculatedArea: false,
          IsRTRL: false
        }
      }
    },
  });

  // Approval Hooks

  useEffect(() => {
    if(userRoleInfo?.length > 0){
      if(userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.local){
        setIsLocalApprover(true);
        setIsRegionApprover(false);
        setIsAgencyApprover(false);
        setIsDepartmentApprover(false);
      } else if(userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.region){
        setIsLocalApprover(true);
        setIsRegionApprover(true);
        setIsAgencyApprover(false);
        setIsDepartmentApprover(false);
      } else if(userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.agency){
        setIsLocalApprover(true);
        setIsRegionApprover(true);
        setIsAgencyApprover(true);
        setIsDepartmentApprover(false);
      } else if(userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.department){
        setIsLocalApprover(true);
        setIsRegionApprover(true);
        setIsAgencyApprover(true);
        setIsDepartmentApprover(true);
      }
    }
  }, [userRoleInfo])

  useEffect(() => {
    if(approverWarningType && !showApproverWarningPopup){
      setShowApproverWarningPopup(true);
    }
  }, [approverWarningType])

  // Grid Load Hooks

  useEffect(() => {
    if(definitionExpression)
    {
      const isValidFilter = doesFilterContainFundingOrgs(definitionExpression);
      if(isValidFilter)
      {
        let year = fiscalYearSelection?.FiscalYear && Number(fiscalYearSelection.FiscalYear);
        // Use the Completion -> Initiation FY setup for now
        const yearClause = (year ? 
        ` AND ((CompletionFiscalYear IS NULL AND InitiationFiscalYear = ${year}) OR (CompletionFiscalYear IS NOT NULL AND CompletionFiscalYear = ${year}))`
        : ``);
  
        // Quicker check here to avoid state change timing causing issues
        const quickCheckFilterHasFiscalYear = definitionExpression.includes("FiscalYear");
  
        let fullDefinitionExpressionTemp = 
        definitionExpression ? 
          "(" + definitionExpression + 
          ` AND (Class in ('Estimated Treatment', 'Estimated Activity'))` +
          ` AND (Status in ('Ready for Approval','Approved (Local)','Approved (Regional)','Approved (Agency)', 'Approved (Department)', 'UnApproval Requested'))` +
          (!quickCheckFilterHasFiscalYear ? yearClause : ``) +
          `)` 
        : "err";
        
        setFullDefinitionExpression(fullDefinitionExpressionTemp);
      }
      else
      {
        displayBulkApprovalFilterWarning(handleClose);
      }
    }
    else
    {
      displayBulkApprovalFilterWarning(handleClose);
    }
  }, [definitionExpression, fiscalYearSelection])

  useEffect(() => {
    if(!isGridQueryLoading && fullDefinitionExpression)
    {
      setIsGridQueryLoading(true);
      let joinedOutFieldStr = featureOutFields.join(",");
      queryAllSummaryService(fullDefinitionExpression, joinedOutFieldStr).then((estFeatures) => {
        if(estFeatures && estFeatures.length > 0)
        {
          // Determine counts
          const estTreatmentCount = estFeatures.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.treatment).length;
          const estActivityCount = estFeatures.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.activity).length;
          setFeatureCounts({    
            TreatmentCount: estTreatmentCount,
            ActivityCount: estActivityCount,
            ActualTreatmentCount: 0,
            ActualActivityCount: 0});

          // Sum up totals
          const contributedLines = estFeatures.filter(si => (si[FieldNames.featureFields.fundingSourceCategory] && 
            si[FieldNames.featureFields.fundingSourceCategory].includes(FieldNames.contributedCheck.contributed)));
          const bilLines = estFeatures.filter(si => (si[FieldNames.featureFields.fundingSourceCategory] && 
            !si[FieldNames.featureFields.fundingSourceCategory].includes(FieldNames.contributedCheck.contributed) && 
            si[FieldNames.featureFields.fundingSourceCategory].includes(FieldNames.contributedCheck.bil)));
          const nonBilLines = estFeatures.filter(si => (si[FieldNames.featureFields.fundingSourceCategory] && 
            !si[FieldNames.featureFields.fundingSourceCategory].includes(FieldNames.contributedCheck.contributed) && 
            !si[FieldNames.featureFields.fundingSourceCategory].includes(FieldNames.contributedCheck.bil)));
          // Cost Calculations
          const totalBilCost = bilLines.map(item => item[FieldNames.fundingFields.totalCost]).reduce((a, b) => a + b, 0);
          const totalNonBilCost = nonBilLines.map(item => item[FieldNames.fundingFields.totalCost]).reduce((a, b) => a + b, 0);
          const totalContributedCost = contributedLines.map(item => item[FieldNames.fundingFields.totalCost]).reduce((a, b) => a + b, 0);
          const totalTotalCost = estFeatures.map(item => item[FieldNames.fundingFields.totalCost]).reduce((a, b) => a + b, 0);
          // Acre Calculations
          const totalBilCalculatedArea = bilLines.map(item => item[FieldNames.featureFields.calculatedAcresFundingSingleLineView]).reduce((a, b) => a + b, 0);
          const totalRoundedBilCalArea = Math.round(totalBilCalculatedArea * 100) / 100;
          const totalContributedCalculatedArea = contributedLines.map(item => item[FieldNames.featureFields.calculatedAcresFundingSingleLineView]).reduce((a, b) => a + b, 0);
          const totalNonBilCalculatedArea = nonBilLines.map(item => item[FieldNames.featureFields.calculatedAcresFundingSingleLineView]).reduce((a, b) => a + b, 0);
          const totalRoundedNonBilCalArea = Math.round((totalNonBilCalculatedArea + totalContributedCalculatedArea) * 100) / 100
          const totalCalculatedArea = estFeatures.map(item => item[FieldNames.featureFields.calculatedAcresFundingSingleLineView]).reduce((a, b) => a + b, 0);
          const totalRoundedCalArea = Math.round(totalCalculatedArea * 100) / 100
          setAllBilCost(totalBilCost);
          setAllNonBilCost(totalNonBilCost);
          setAllContributedCost(totalContributedCost);
          setAllTotalCost(totalTotalCost);
          setAllBilAcres(totalRoundedBilCalArea);
          setAllNonBilAcres(totalRoundedNonBilCalArea);
          setAllTotalAcres(totalRoundedCalArea);

          // Determine if Approval level is valid
          if(userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.agency){
            const allAgencyVals = estFeatures.map(x => x[FieldNames.featureFields.fundingAgency]);
            const distinctAgencies = [...new Set(allAgencyVals)];
            checkMultiValueApproval(distinctAgencies);
          } else if(userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.region){
            const allRegionVals = estFeatures.map(x => x[FieldNames.featureFields.fundingRegion]);
            const distinctRegions = [...new Set(allRegionVals)];
            checkMultiValueApproval(distinctRegions);
          }

          // Perform grid specific calculations
          let rowCount = 1;
          let actionDictionaryCount = {};
          estFeatures.forEach((element, index) => { 
            estFeatures[index].internalId = `${element[FieldNames.featureFields.class]} - ` + rowCount; 
            estFeatures[index].GroupingID = `${element[FieldNames.featureFields.id]}`; 
            estFeatures[index][FieldNames.featureFields.name] = `${element[FieldNames.featureFields.name]} (${element[FieldNames.featureFields.id]})`; 
            const doesActionExistInDictionary = actionDictionaryCount.hasOwnProperty(estFeatures[index][FieldNames.featureFields.name]);
            //clonedActionsWithFunding[index][FieldNames.featureFields.calculatedAcresFundingSingleLineView] = !doesActionExistInDictionary ? clonedActionsWithFunding[index][FieldNames.featureFields.calculatedAcresFundingSingleLineView] : null;
            if(doesActionExistInDictionary)
            {
              actionDictionaryCount[estFeatures[index][FieldNames.featureFields.name]] = 
                actionDictionaryCount[estFeatures[index][FieldNames.featureFields.name]] + 1
            }
            else
            {
              actionDictionaryCount[estFeatures[index][FieldNames.featureFields.name]] =  1;
            }
          });

          // Set list of values
          setGridValues(estFeatures);
        }
        else{
          setFeatureCounts({    
            TreatmentCount: 0,
            ActivityCount: 0,
            ActualTreatmentCount: 0,
            ActualActivityCount: 0});
          setAllBilCost(0);
          setAllNonBilCost(0);
          setAllContributedCost(0);
          setAllTotalCost(0);
          setAllBilAcres(0);
          setAllNonBilAcres(0);
          setAllTotalAcres(0);
          setGridValues([]);
        }
        setIsGridQueryLoading(false);
      }).catch(error => {
        setIsGridQueryLoading(false);
        ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
      });
    }
    else
    {
      setFeatureCounts({    
        TreatmentCount: 0,
        ActivityCount: 0,
        ActualTreatmentCount: 0,
        ActualActivityCount: 0});
      setAllBilCost(0);
      setAllNonBilCost(0);
      setAllContributedCost(0);
      setAllTotalCost(0);
      setAllBilAcres(0);
      setAllNonBilAcres(0);
      setAllTotalAcres(0);
      setGridValues([]);
    }
  }, [fullDefinitionExpression])

  useEffect(() => {
    if (!allSelectedItems?.length || !datePickerValue ||
       (!isLocalDateChecked && !isRegionDateChecked && !isAgencyDateChecked && !isDepartmentDateChecked)) {
      return;
    }

    const valueParse = moment(datePickerValue);
    setItemsAtTimeOfApproval(allSelectedItems.length);
    setIsApprovalDateInFuture(valueParse.isAfter(moment(), 'day'));

    const results = {
      localNotPriorRegional: [],
      regionalNotAfterLocal: [],
      regionalNotPriorAgency: [],
      agencyNotAfterRegional: [],
      agencyNotPriorDepartment: [],
      departmentNotAfterAgency: [],
      valid: []
    };

    allSelectedItems.forEach((item) => {
      const attr = item?.attributes ?? item;
      const getMoment = (field) => attr[field] ? moment(attr[field]) : null;

      const localDate = getMoment(FieldNames.featureFields.localApprovalDate);
      const regionalDate = getMoment(FieldNames.featureFields.regionalApprovalDate);
      const agencyDate = getMoment(FieldNames.featureFields.agencyApprovalDate);
      const deptDate = getMoment(FieldNames.featureFields.departmentApprovalDate);

      if (isLocalDateChecked && regionalDate && valueParse.isAfter(regionalDate, 'day')) {
        results.localNotPriorRegional.push(attr);
      } else if (isRegionDateChecked && localDate && valueParse.isBefore(localDate, 'day')) {
        results.regionalNotAfterLocal.push(attr);
      } else if (isRegionDateChecked && agencyDate && valueParse.isAfter(agencyDate, 'day')) {
        results.regionalNotPriorAgency.push(attr);
      } else if (isAgencyDateChecked && regionalDate && valueParse.isBefore(regionalDate, 'day')) {
        results.agencyNotAfterRegional.push(attr);
      } else if (isAgencyDateChecked && deptDate && valueParse.isAfter(deptDate, 'day')) {
        results.agencyNotPriorDepartment.push(attr);
      } else if (isDepartmentDateChecked && regionalDate && valueParse.isBefore(regionalDate, 'day')) {
        results.departmentNotAfterAgency.push(attr);
      } else {
        results.valid.push(attr);
      }
    });

    setSelectedItemsLocalNotPriorRegional(results.localNotPriorRegional);
    setSelectedItemsRegionalNotAfterLocal(results.regionalNotAfterLocal);
    setSelectedItemsRegionalNotPriorAgency(results.regionalNotPriorAgency);
    setSelectedItemsAgencyNotAfterRegional(results.agencyNotAfterRegional);
    setSelectedItemsAgencyNotPriorDepartment(results.agencyNotPriorDepartment);
    setSelectedItemsDepartmentNotAfterAgency(results.departmentNotAfterAgency);
    setValidSelectedItems(results.valid);

  }, [allSelectedItems, datePickerValue, isLocalDateChecked, 
      isRegionDateChecked, isAgencyDateChecked, isDepartmentDateChecked])

  // ------------
  //  Page Logic
  // ------------

  // Navigation Logic

  const handleClose = () => {
    dispatch(setShowMap(true));
    navigate("/home");
  };

  // Grid Setup Logic
  const defineColumns = useCallback(() => {
    const displayableFields = [
      "GroupingID", 
      "ID",
      "Name",
      FieldNames.featureFields.fundingSourceProgram,
      FieldNames.featureFields.fundingSourceCategory,
      FieldNames.featureFields.fundingSourceSubCategory,
      FieldNames.featureFields.initiationFiscalYear,
      FieldNames.featureFields.fundingAgency,
      FieldNames.featureFields.fundingRegion,
      FieldNames.featureFields.fundingUnit,
      FieldNames.featureFields.class,
      FieldNames.fundingFields.totalCost, 
      FieldNames.featureFields.localApprovalDate, 
      FieldNames.featureFields.regionalApprovalDate,
      FieldNames.featureFields.agencyApprovalDate,
      FieldNames.featureFields.departmentApprovalDate,
      FieldNames.featureFields.estimatedSuccessProbability,
      FieldNames.featureFields.feasibility,
      FieldNames.featureFields.priority,
      FieldNames.featureFields.isRTRL
    ];

    const columnWidth = (element) => {
      if(element === FieldNames.featureFields.name){
        return 250
      } else if(element === FieldNames.featureFields.regionalApprovalDate ||
        element === FieldNames.featureFields.localApprovalDate || element === FieldNames.featureFields.priority ||
        element === FieldNames.featureFields.agencyApprovalDate || element === FieldNames.featureFields.departmentApprovalDate){
        return 100
      } else if(element === FieldNames.fundingFields.totalCost || element === FieldNames.fundingFields.assetCost || 
        element === FieldNames.fundingFields.contractualCost || element === FieldNames.fundingFields.grantsFixedCost ||
        element === FieldNames.fundingFields.otherCost || element === FieldNames.fundingFields.personnelCost) {
          return 100
      } else if(element === FieldNames.featureFields.calculatedAcresFundingSingleLineView) {
        return 100;
      } else if(element === FieldNames.featureFields.initiationFiscalYear || element === FieldNames.featureFields.completionFiscalYear) {
        return 100
      } else if(element === FieldNames.featureFields.estimatedSuccessProbability || element === FieldNames.featureFields.feasibility){
        return 100
      } else if(element === FieldNames.featureFields.class){
        return 100
      } else {
        return 130
      }
    }

    const columns = [];


    displayableFields.forEach((element) => {
      if(element === "GroupingID"){
        columns.push( 
          {
            field: element,
            headerName: "GroupingID",
            groupable: false,
            hideable: false,
            sortable: true,
            aggregable: true,
            type: 'string',
            width: 250,
            renderCell: (params) => <a href={getURLPath(params.row, element)}>{formatResponse(params.row, element)}</a>,
          }
        )
      }
      else if(element === "ID")
        {
          columns.push( 
            {
              field: element,
              headerName: "Hotlink",
              groupable: true,
              hideable: false,
              sortable: true,
              editable: false,
              width: 250,
              valueGetter: (value, row) => { 
                return formatResponse(row, element)
              },
              renderCell: (params) => <a href={getURLPath(params.row, element)}>{formatResponse(params.row, element)}</a>,
            }
          )
        } else {
        const currentfieldInfo = fieldsDefinition[element];
        let headerName = currentfieldInfo ? currentfieldInfo.alias : "";
        if(currentfieldInfo && currentfieldInfo.name === FieldNames.featureFields.calculatedAcresFundingSingleLineView) {
          columns.push( 
            {
              field: element,
              headerName: FieldNames.bulkApprovalAbbreviations.calculatedArea,
              sortable: true,
              groupable: false,
              editable: false,
              type: 'number',
              width: columnWidth(element),
            }
          )
        } else if(currentfieldInfo && currentfieldInfo.name === FieldNames.fundingFields.totalCost){
          columns.push( 
            {
              field: element,
              headerName: FieldNames.bulkApprovalAbbreviations.estimatedTotalCost,
              sortable: true,
              groupable: false,
              editable: false,
              type: 'number',
              width: columnWidth(element),
              valueFormatter: (value, row, column) => {
                if(value === 0){
                  return '$0'
                } else {
                  if(!value) {
                    return '';
                  } else {
                    return currencyFormatter.format(value);
                  }
                }
              }
            }
          )
        }
        else if(currentfieldInfo && currentfieldInfo.name === FieldNames.featureFields.isRTRL){
          columns.push( 
            {
              field: element,
              headerName: FieldNames.featureFields.isRTRL,
              sortable: true,
              groupable: false,
              editable: false,
              hideable: true,
              aggregable: false,
              type: 'string',
              width: columnWidth(element),
              valueGetter: (value, row) => { 
                return formatResponse(row, element)
              },
            }
          )
        } else {
          let isHideable = true;
          if(currentfieldInfo){
            if(currentfieldInfo.name === FieldNames.featureFields.name){
              headerName = "Name"
            } else if(currentfieldInfo.name === FieldNames.featureFields.estimatedSuccessProbability){
              headerName = FieldNames.bulkApprovalAbbreviations.estimatedSuccessProbability;
            } else if(currentfieldInfo.name === FieldNames.featureFields.feasibility){
              headerName = FieldNames.bulkApprovalAbbreviations.feasibility;
            } else if(currentfieldInfo.name === "Checkbox selection"){
              isHideable = false;
            } else if(currentfieldInfo.name === FieldNames.featureFields.priority){
              headerName = FieldNames.bulkApprovalAbbreviations.priority;
            } else if(currentfieldInfo.name === FieldNames.featureFields.department){
              headerName = FieldNames.bulkApprovalAbbreviations.department;
            } else if(currentfieldInfo.name === FieldNames.featureFields.agency){
              headerName = FieldNames.bulkApprovalAbbreviations.agency;
            } else if(currentfieldInfo.name === FieldNames.featureFields.region){
              headerName = FieldNames.bulkApprovalAbbreviations.region;
            } else if(currentfieldInfo.name === FieldNames.featureFields.unit){
              headerName = FieldNames.bulkApprovalAbbreviations.unit;
            } else if(currentfieldInfo.name === FieldNames.featureFields.initiationFiscalYear){
              headerName = FieldNames.bulkApprovalAbbreviations.initiationFiscalYear;
            } else if(currentfieldInfo.name === FieldNames.featureFields.fundingSourceProgram){
              headerName = FieldNames.bulkApprovalAbbreviations.fundingSourceProgram;
            } else if(currentfieldInfo.name === FieldNames.featureFields.fundingSourceCategory){
              headerName = FieldNames.bulkApprovalAbbreviations.fundingSourceCategory;
            } else if(currentfieldInfo.name === FieldNames.featureFields.fundingSourceSubCategory){
              headerName = FieldNames.bulkApprovalAbbreviations.fundingSourceSubCategory;
            }
          }
          columns.push( 
            {
              field: element,
              headerName: headerName,
              hideable: isHideable,
              aggregable: true,
              sortable: true,
              editable: false,
              width: columnWidth(element),
              valueGetter: (value, row) => { 
                return formatResponse(row, element)
              },
            }
          )
        }
      }
    });
    setGridColumns(columns);
  }, []);

  const handleIsRowSelectable = (params) => {
    //Must always keep the id
    const currentRow = apiRef.current.getRowNode(params.id);
    if(rowGroupingModel && rowGroupingModel.length > 0){
      const rowGroupingMinusId = rowGroupingModel.filter(x => x !== 'id');
      if(rowGroupingMinusId && rowGroupingMinusId.length > 0 && rowGroupingMinusId.includes("Name")){
        return currentRow.depth === 0;
      } else if(rowGroupingMinusId.length > 0) {
        return currentRow.depth >= rowGroupingMinusId.length;
      }
      else
      {
        return true;
      }
    }
  }

  // Bulk Approval Logic

  const handleApproverWarningClose = () => {
    setShowApproverWarningPopup(false);
    handleClose()
  }

  const handleContinue = () => {
    setShowApproverWarningPopup(false);
  }

  const closeApprovalDateConfirmationDialog = () => {
    setIsApprovalDateConfirmationDialogOpen(false);
  }

  const confirmAssignApprovalDates = () => {
    // Adjust to only send through valid entries
    changingApproval(true, validSelectedItems);
  }

  const checkMultiValueApproval = (distinctMultiValues) => {
    if(distinctMultiValues?.length > 0 && userRoleInfo)
    {
      if(userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.agency || userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.region){
        if(distinctMultiValues.length > 1 || (distinctMultiValues.length > 0 && distinctMultiValues.includes("|"))){
          if(!showApproverWarningPopup){
            setShowApproverWarningPopup(true);
          }
          if(userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.agency){
            setApproverWarningType("Funding Agencies");
          } else if(userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.region){
            setApproverWarningType("Funding Regions");
          }
        }
      }
    }  
  }
  
  const updateGridValues = (actionItemsToUpdate, auditMessage, setIsSelectAllLoading) => {
    let newGridValues = JSON.parse(JSON.stringify(gridValues));
    if(actionItemsToUpdate?.length > 0){
      
      //Only save the fields that are changing
      let preUpdateActionItems = [];
      actionItemsToUpdate.forEach((element, index) => {
        const obj = {
          [FieldNames.featureFields.objectID]: element[FieldNames.featureFields.objectID],
          [FieldNames.featureFields.localApprovalDate]: element[FieldNames.featureFields.localApprovalDate],
          [FieldNames.featureFields.regionalApprovalDate]: element[FieldNames.featureFields.regionalApprovalDate],
          [FieldNames.featureFields.agencyApprovalDate]: element[FieldNames.featureFields.agencyApprovalDate],
          [FieldNames.featureFields.departmentApprovalDate]: element[FieldNames.featureFields.departmentApprovalDate],
          [FieldNames.featureFields.lastModifiedDate]: element[FieldNames.featureFields.lastModifiedDate],
          [FieldNames.featureFields.modifiedBy]: element[FieldNames.featureFields.modifiedBy],
          [FieldNames.featureFields.status]: element[FieldNames.featureFields.status],
        }
        preUpdateActionItems.push(obj)
      })

      FeatureService.updateMultipleFeatures(preUpdateActionItems).then(actionResponse => {
        let areActionsAtLeastPartiallySuccessful = isUpdatePartiallySuccessful(actionResponse);
        let areActionsFullySuccessful = isUpdateFullySuccessful(actionResponse);
        let allActionItems = [];
        if (areActionsAtLeastPartiallySuccessful || areActionsFullySuccessful) {
          allActionItems = modifySelectedItems(newGridValues, actionItemsToUpdate);
          AuditLogService.addMultipleAuditLogs(
            FieldNames.auditLogJoinTypes.polygon,
            allActionItems,
            userInfo[FieldNames.userFields.nifcUsername],
            auditMessage
          ).then((response) => {
            // Nothing for now
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
          setIsBulkApprovalLoading(false);
          if(setIsSelectAllLoading){
            setIsSelectAllLoading(false)
          }
          if(areActionsFullySuccessful){
            setGridValues(allActionItems);
            setSnackbarMessage("All Actions updated successfully");
            setSnackbarOpen(true);
            setSnackbarSeverity("success");
          } else if(areActionsAtLeastPartiallySuccessful){
            setGridValues(allActionItems);
            setSnackbarMessage(`Some Actions updated successfully, Total Count of failures are: ${actionResponse.failures.length}`);
            setSnackbarOpen(true);
            setSnackbarSeverity("warning");
          } else {
            setSnackbarMessage("There was an issue and Actions weren't updated");
            setSnackbarOpen(true);
            setSnackbarSeverity("error");
          }
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
      });
    } else {
      setIsBulkApprovalLoading(false);
      if(setIsSelectAllLoading){
        setIsSelectAllLoading(false)
      }
    }
  }

  const modifySelectedItems = (newGridValues, updatedFeatureItems) => {
    let allItems = [];
    newGridValues.forEach(gridVal => {
      if(gridVal && updatedFeatureItems?.length > 0){
        let updatedItems = updatedFeatureItems.filter(x => x && x[FieldNames.featureFields.joinID] === gridVal[FieldNames.featureFields.joinID] && 
          x[FieldNames.featureFields.joinFeature] === gridVal[FieldNames.featureFields.joinFeature]);
        if(updatedItems && updatedItems.length > 0){
          const updatedItem = updatedItems[0];
          gridVal = getGridFieldValues(gridVal, updatedItem);
        }
      }
      allItems.push(gridVal);
    });
    
    return allItems;
  }

  const getGridFieldValues = (gridVal, updatedItem) => {
    //hasOwnProperty check prevents the grid from looking like items have been cleared or populated when they haven't
    if(updatedItem.hasOwnProperty(FieldNames.featureFields.localApprovalDate)){
      gridVal[FieldNames.featureFields.localApprovalDate] = updatedItem[FieldNames.featureFields.localApprovalDate];
    }
    if(updatedItem.hasOwnProperty(FieldNames.featureFields.regionalApprovalDate)){
      gridVal[FieldNames.featureFields.regionalApprovalDate] = updatedItem[FieldNames.featureFields.regionalApprovalDate];
    }
    if(updatedItem.hasOwnProperty(FieldNames.featureFields.agencyApprovalDate)){
      gridVal[FieldNames.featureFields.agencyApprovalDate] = updatedItem[FieldNames.featureFields.agencyApprovalDate];
    }
    if(updatedItem.hasOwnProperty(FieldNames.featureFields.departmentApprovalDate)){
      gridVal[FieldNames.featureFields.departmentApprovalDate] = updatedItem[FieldNames.featureFields.departmentApprovalDate];
    }
    gridVal[FieldNames.featureFields.modifiedBy] = updatedItem[FieldNames.featureFields.modifiedBy];
    gridVal[FieldNames.featureFields.lastModifiedDate] = updatedItem[FieldNames.featureFields.lastModifiedDate];
    if(updatedItem.hasOwnProperty(FieldNames.featureFields.unApprovalDate)){
      gridVal[FieldNames.featureFields.unApprovalDate] = updatedItem[FieldNames.featureFields.unApprovalDate];
    }
    return gridVal;
  }

  const changingApproval = (isFromApprove, validValues = null) => {
    let isApproving = isFromApprove && datePickerValue;
    let isApprovingMessage = isApproving ? 'Approved' : 'Unapproved';
    let auditMessage = `${userInfo[FieldNames.userFields.nifcUsername]} ${isApprovingMessage} `;
    let isSecondApprovalType = false;
    if(isLocalDateChecked)
    {
      auditMessage += `Local`;
      isSecondApprovalType = true;
    }
    if(isRegionDateChecked)
    {
      auditMessage += isSecondApprovalType ? `, ` : ``;
      auditMessage += `Region`;
      isSecondApprovalType = true;
    }
    if(isAgencyDateChecked)
    {
      auditMessage += isSecondApprovalType ? `, ` : ``;
      auditMessage += `Agency`;
      isSecondApprovalType = true;
    }
    if(isDepartmentDateChecked)
    {
      auditMessage += isSecondApprovalType ? `, ` : ``;
      auditMessage += `Department`;
    }

    if(isApproving){
      handleBulkChanges(approveItems, auditMessage, validValues);
    } else {
      handleBulkChanges(unApproveItems, auditMessage, validValues);
    }
  }

  const requestUnapproval = (validValues) => {
    let auditMessage = `${userInfo[FieldNames.userFields.nifcUsername]} requested UnApproval`;
    handleBulkChanges(requestUnapprovalForItem, auditMessage, validValues);
  }

  const handleBulkChanges = (updateMethod, auditMessage, validValues) => {
    setIsBulkApprovalLoading(true);
    setBulkApprovalErrors([]);
    let actionItemsToUpdate = [];
    let itemArr = validValues?.length > 0 ? validValues : allSelectedItems;
    itemArr.forEach(element => {
      let currentFeature = gridValues.filter(action => action[FieldNames.featureFields.joinID] === element[FieldNames.featureFields.joinID] && 
        action[FieldNames.featureFields.joinFeature] === element[FieldNames.featureFields.joinFeature]);

      let isAddedAlready = false;
      if(actionItemsToUpdate?.length > 0){
        const matchingItems = actionItemsToUpdate.filter(x => x[FieldNames.featureFields.joinID] === element[FieldNames.featureFields.joinID] && 
          x[FieldNames.featureFields.joinFeature] === element[FieldNames.featureFields.joinFeature]);
        if(matchingItems && matchingItems.length > 0){
          isAddedAlready = true;
        }
      }
      if(!isAddedAlready){
        currentFeature = updateMethod(element, currentFeature);
        actionItemsToUpdate.push(currentFeature);
      }
    });
    updateGridValues(actionItemsToUpdate, auditMessage, null);
  }

  const approveItems = (element, currentFeature) => {
    //Setting this up to be used by both all and selected
    const selectedDate = getSaveableDate(datePickerValue);
    let now = getSaveableDate(new Date());
    let featureItem;
    if(currentFeature?.length){
      featureItem = currentFeature[0];
    } else {
      featureItem = {}
      featureItem[FieldNames.featureFields.objectID] = element[FieldNames.featureFields.objectID]
      featureItem[FieldNames.featureFields.class] = element[FieldNames.featureFields.class]
    }

    if(isLocalDateChecked){
      featureItem[FieldNames.featureFields.localApprovalDate] = selectedDate;
      featureItem[FieldNames.featureFields.modifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
      featureItem[FieldNames.featureFields.lastModifiedDate] = now;
    }

    if(isRegionDateChecked){
      featureItem[FieldNames.featureFields.regionalApprovalDate] = selectedDate;
      featureItem[FieldNames.featureFields.modifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
      featureItem[FieldNames.featureFields.lastModifiedDate] = now;
    }

    if(isAgencyDateChecked){
      featureItem[FieldNames.featureFields.agencyApprovalDate] = selectedDate;
      featureItem[FieldNames.featureFields.modifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
      featureItem[FieldNames.featureFields.lastModifiedDate] = now;
    }

    if(isDepartmentDateChecked){
      featureItem[FieldNames.featureFields.departmentApprovalDate] = selectedDate;
      featureItem[FieldNames.featureFields.modifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
      featureItem[FieldNames.featureFields.lastModifiedDate] = now;
    }
    const newStatus = getStatusDirect(featureItem, false, false, 0, null, null);
    featureItem[FieldNames.featureFields.status] = newStatus;
    return featureItem;
  }

  const unApproveItems = (element, currentFeature) => {
    let featureItem;
    if(currentFeature?.length){
      featureItem = currentFeature[0];
    } else {
      featureItem = {}
      featureItem[FieldNames.featureFields.objectID] = element[FieldNames.featureFields.objectID]
      featureItem[FieldNames.featureFields.class] = element[FieldNames.featureFields.class]
    }
    if(isLocalDateChecked){
      featureItem[FieldNames.featureFields.localApprovalDate] = null;
      featureItem[FieldNames.featureFields.modifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
      featureItem[FieldNames.featureFields.lastModifiedDate] = getSaveableDate(new Date());
    } 
    
    if(isRegionDateChecked){
      featureItem[FieldNames.featureFields.regionalApprovalDate] = null;
      featureItem[FieldNames.featureFields.modifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
      featureItem[FieldNames.featureFields.lastModifiedDate] = getSaveableDate(new Date());
    } 
    
    if(isAgencyDateChecked){
      featureItem[FieldNames.featureFields.agencyApprovalDate] = null;
      featureItem[FieldNames.featureFields.modifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
      featureItem[FieldNames.featureFields.lastModifiedDate] = getSaveableDate(new Date());
    } 
    
    if(isDepartmentDateChecked){
      featureItem[FieldNames.featureFields.departmentApprovalDate] = null;
      featureItem[FieldNames.featureFields.modifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
      featureItem[FieldNames.featureFields.lastModifiedDate] = getSaveableDate(new Date());
    }

    const newStatus = getStatusDirect(featureItem, false, false, 0, null, null);
    featureItem[FieldNames.featureFields.status] = newStatus;
    return featureItem;
  }

  const requestUnapprovalForItem = (element, currentFeature) => {
    let featureItem;
    if(currentFeature?.length){
      featureItem = currentFeature[0];
    } else {
      featureItem = {}
      featureItem[FieldNames.featureFields.objectID] = element[FieldNames.featureFields.objectID]
      featureItem[FieldNames.featureFields.class] = element[FieldNames.featureFields.class]
    }
    featureItem[FieldNames.featureFields.unApprovalDate] = getSaveableDate(new Date());
    featureItem[FieldNames.featureFields.modifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
    featureItem[FieldNames.featureFields.lastModifiedDate] = getSaveableDate(new Date());
    const newStatus = getStatusDirect(featureItem, false, false, 0, null, null);
    featureItem[FieldNames.featureFields.status] = newStatus;
    return featureItem;
  }

  //-----------------------
  // Secondary Hook Section
  // ----------------------

  // Hooks that need above methods defined
  useEffect(()=>{
    moment.tz.setDefault('America/New_York');
    defineColumns();
  },[defineColumns])

  // --- Grid Logic ---

  function CustomExportToolbar(props) {
    return (
      <GridToolbarContainer {...props}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
      </GridToolbarContainer>
    );
  }

  const confirmationBulkApprovalContents = (isAll, confirmationType) => {
    let itemCountAtTimeOfApproval = itemsAtTimeOfApproval;
    let validSelectedItemsCount = validSelectedItems.length;
    if(isAll){
      itemCountAtTimeOfApproval = featureCounts.TreatmentCount + featureCounts.ActivityCount;
      validSelectedItemsCount = gridValues.length;
    }
    if(confirmationType === "approval"){
      return (
        <Box>
          <Typography
            sx={{ marginLeft: "5px" }}
            fontWeight={"bold"}
          >
            You have selected {itemCountAtTimeOfApproval} row(s) to be marked as approved, of which {validSelectedItemsCount} are valid.
          </Typography>
          {
            isApprovalDateInFuture ?
            <Typography
              sx={{ marginLeft: "5px" }}
              fontWeight={"bold"}
              color={"red"}
            >
              Error: The selected approval date may not be in the future, please review before continuing.
            </Typography> : null
          }
          {
            isGridLoading ?
            <CircularProgress/> :
            !isApprovalDateInFuture && validSelectedItemsCount > 0 && bulkApprovalErrors.length === 0 ?
            <Typography
              sx={{ marginLeft: "5px" }}
              fontWeight={"bold"}
              color={"green"}
            >
              All valid rows have been approved
            </Typography> : 
          bulkApprovalErrors.length > 0 ?
          <>
            <Typography
              sx={{ marginLeft: "5px" }}
              fontWeight={"bold"}
              color={"red"}
            >
              There were issues with the approval process.
            </Typography>
            <Typography
              sx={{ marginLeft: "5px" }}
              fontWeight={"bold"}
              color={"red"}
            >
              {bulkApprovalErrors[0]}
            </Typography>
          </>
          : null
          }
          {
            selectedItemsLocalNotPriorRegional && selectedItemsLocalNotPriorRegional.length > 0 ?
            <>
              <Typography
                sx={{ marginLeft: "5px" }}
                fontWeight={"bold"}
                color={"red"}
              >
                Error: The Local Approval Date must be the same date or prior to the Regional Approval Date assigned to {selectedItemsLocalNotPriorRegional.length} of the selected feature(s).
              </Typography> 
            </>
            : null
          }
          {
            selectedItemsRegionalNotAfterLocal && selectedItemsRegionalNotAfterLocal.length > 0 ?
            <>
              <Typography
                sx={{ marginLeft: "5px" }}
                fontWeight={"bold"}
                color={"red"}
              >
                Error: The Regional Approval Date must be the same date or following the Local Approval Date assigned to {selectedItemsRegionalNotAfterLocal.length} of the following feature(s).
              </Typography> 
            </>
            : null
          }
          {
            selectedItemsRegionalNotPriorAgency && selectedItemsRegionalNotPriorAgency.length > 0 ?
            <>
              <Typography
                sx={{ marginLeft: "5px" }}
                fontWeight={"bold"}
                color={"red"}
              >
                Error: The Regional Approval Date must be the same date or prior to the Agency Approval Date assigned to {selectedItemsRegionalNotPriorAgency.length} of the following feature(s).
              </Typography> 
              </>
            : null
          }
          {
            selectedItemsAgencyNotAfterRegional && selectedItemsAgencyNotAfterRegional.length > 0 ?
            <>
              <Typography
                sx={{ marginLeft: "5px" }}
                fontWeight={"bold"}
                color={"red"}
              >
                Error: The Agency Approval Date must be the same date or following the Regional Approval Date assigned to {selectedItemsAgencyNotAfterRegional.length} of the following feature(s).
              </Typography> 
            </>
            : null
          }
          {
            selectedItemsAgencyNotPriorDepartment && selectedItemsAgencyNotPriorDepartment.length > 0 ?
            <>
              <Typography
                sx={{ marginLeft: "5px" }}
                fontWeight={"bold"}
                color={"red"}
              >
                Error: The Agency Approval Date must be the same date or prior to the Department Approval Date to {selectedItemsAgencyNotPriorDepartment.length} of the following feature(s).
              </Typography> 
            </>
            : null
          }
          {
            selectedItemsDepartmentNotAfterAgency && selectedItemsDepartmentNotAfterAgency.length > 0 ?
            <>
              <Typography
                sx={{ marginLeft: "5px" }}
                fontWeight={"bold"}
                color={"red"}
              >
                Error: The Department Approval Date must be the same date or following the Agency Approval Date assigned to {selectedItemsDepartmentNotAfterAgency.length} of the following feature(s).
              </Typography> 
             {/* grid */}
            </>
            : null
          }
          {
            (disableApprovalConfirm || isAll) ? null :
            <Typography
              sx={{ marginLeft: "5px" }}
              fontWeight={"bold"}
            >
              Please review and confirm to continue with the approval.
            </Typography>
          }
        </Box>
      )
    } else if(confirmationType === "unapproval") {
      return (
        <Box>
          <Typography
            sx={{ marginLeft: "5px" }}
            fontWeight={"bold"}
          >
            You have selected {itemCountAtTimeOfApproval} row(s) to be marked as unapproved.
          </Typography>
          
          {
            isGridLoading ?
            <CircularProgress/> :
            !isApprovalDateInFuture && validSelectedItemsCount > 0 && bulkApprovalErrors.length === 0 ?
            <Typography
              sx={{ marginLeft: "5px" }}
              fontWeight={"bold"}
              color={"green"}
            >
              All valid rows have been unapproved
            </Typography> : 
          bulkApprovalErrors.length > 0 ?
          <>
            <Typography
              sx={{ marginLeft: "5px" }}
              fontWeight={"bold"}
              color={"red"}
            >
              There were issues with the unapproval process.
            </Typography>
            <Typography
              sx={{ marginLeft: "5px" }}
              fontWeight={"bold"}
              color={"red"}
            >
              {bulkApprovalErrors[0]}
            </Typography>
          </>
          : null
          }
        </Box>
      )
    } else if(confirmationType === "reqUnapproval"){
      return (
          <Box>
              <Typography sx={{ marginLeft: '5px' }} fontWeight={'bold'}>
                  You have selected {itemCountAtTimeOfApproval} row(s) to be assigned a Request of
                  Unapproved.
              </Typography>

              {isGridLoading ? (
                  <CircularProgress />
              ) : validSelectedItemsCount > 0 && bulkApprovalErrors.length === 0 ? (
                  <Typography sx={{ marginLeft: '5px' }} fontWeight={'bold'} color={'green'}>
                      All valid rows have had a Request of Unapproved assigned
                  </Typography>
              ) : bulkApprovalErrors.length > 0 ? (
                  <>
                      <Typography sx={{ marginLeft: '5px' }} fontWeight={'bold'} color={'red'}>
                          There were issues with the Requesting an Unapproval process.
                      </Typography>
                      <Typography sx={{ marginLeft: '5px' }} fontWeight={'bold'} color={'red'}>
                          {bulkApprovalErrors[0]}
                      </Typography>
                  </>
              ) : null}
          </Box>
      );
    } else {
      return (<div></div>)
    }
   }

  return(
    <Box>
      <AppBar
        sx={{ position: "relative" }}
        style={{ backgroundColor: ColorConstants.BlueAlien, marginTop: headerHeight }}
      >
        <Toolbar>
          <FormControl variant="standard" className={classes.formControl}>
            <ThemeProvider theme={theme}>
              <Grid
                item
                size={12}
                
                container
                direction="row"
                className="grid-item"
                style={{ alignItems: "center" }}
              >
                <Grid item size={0.2}>
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={handleClose}
                    aria-label="close"
                  >
                    <CloseIcon style={{ fill: ColorConstants.WhiteAlien }} />
                  </IconButton>
                </Grid>
                <Grid item size={4}>
                  <Typography
                    sx={{ ml: 2, flex: 1 }}
                    variant="h6"
                    component="div"
                    style={{ color: ColorConstants.WhiteAlien }}
                  >
                    Bulk Approval
                  </Typography>
                </Grid>
                <WorkflowFiscalYearFilter
                  fiscalYearSelection={fiscalYearSelection}
                  setFiscalYearSelection={setFiscalYearSelection}
                  fiscalYearOptions={fiscalYearOptions}
                  isDisabled={filterHasFiscalYear}
                  disabledText={"This is disabled because the applied filter contains a fiscal year element."}
                />
              </Grid>
            </ThemeProvider>
          </FormControl>
        </Toolbar>
      </AppBar>
      <Paper style={{ maxHeight: "77.5vh", overflow: "auto" }}>
        <InFormAlert 
          openFlag={showApproverWarningPopup} 
          closeMethod={handleApproverWarningClose}
          buttonMethod={handleContinue}
          titleText={"Bulk Approval Warning"}
          contentText={`Warning: The data you have selected contains funding from multiple ${approverWarningType}. If that is not intentional, please go back and apply the appropriate filters.`}
        />
        <InFormGenericModal
        openFlag={isApprovalDateConfirmationDialogOpen}
        closeMethod={closeApprovalDateConfirmationDialog}
        titleText={"Confirmation of Bulk Approval"}
        displayButton={false}
        >
          <Box
            style={{
              maxHeight: "75vh",
              overflow: "auto",
              maxWidth: "50vw",
              minWidth: "50vw",
            }}
          >
            <Paper elevation={6} style={{ padding: 20, margin: 20 }}>
              {confirmationBulkApprovalContents(false, "approval")}
              <Box sx={{ marginLeft: "80%", marginRight: "20%", marginBottom: "5px" }}>
              <Button
                variant="contained"
                disabled={disableApprovalConfirm}
                onClick={() => {
                  setIsApprovalDateConfirmationDialogOpen(false);
                }}
                aria-label="edit"
                sx={{ marginLeft: "5px" }}
              >
                Continue
              </Button>
            </Box>
            </Paper>
          </Box>
        </InFormGenericModal>
        <Box
          sx={{
            height: "900px", //525
            width: "100%",
            minWidth: "10px",
            paddingBottom: "15vh !important",
          }}
        >
          <div style={{ padding: "5px 7px", color: ColorConstants.GrayAlien}}>
            Information below only includes “estimated” information. This screen should not be used to understand actual accomplishments or expenditures.
          </div>
          <Box
            sx={{position: "absolute", left: "7px"}}
          >
            <CurrentFilterExpressionPopup/>
          </Box>
          <Grid container sx={{paddingTop: "25px"}}>
            <Grid item size={{sm: 12, md: 6}} style={{ marginBottom: 15}}>
              <Grid container spacing={0.5} width={ (width - 100) } style={{ paddingTop: 10, paddingLeft: 20 }}>
                <Grid item size={{sm: 12, md: 6}}>
                  <h3 style={{margin: '7px 0 0 12px'}}>Selected Totals</h3>
                </Grid>
              </Grid>
              <Grid container spacing={0.5} width={ (width - 100) } style={{ paddingTop: 10, paddingLeft: 20 }}>
                <Grid item size={6}>
                  <p style={{ margin: '7px 0px 0px 12px' }}>Selected BIL Funding Subtotal: <strong>${commaFormatter(bilCost)}</strong></p>
                </Grid>
                <Grid item size={6}>
                  <p style={{ margin: '7px 0px 0px 0px' }}>Selected BIL Acres Subtotal: <strong>{commaFormatter(bilAcres)}</strong></p>
                </Grid>
              </Grid>
              <Grid container spacing={0.5} width={ (width - 100) } style={{ paddingTop: 10, paddingLeft: 20 }}>
                <Grid item size={6}>
                  <p style={{ margin: '7px 0px 0px 12px' }}>Selected non-BIL Funding Subtotal: <strong>${commaFormatter(nonBilCost)}</strong></p>
                </Grid>
                <Grid item size={6}>
                  <p style={{ margin: '7px 0px 0px 0px' }}>Selected non-BIL Acres Subtotal: <strong>{commaFormatter(nonBilAcres)}</strong></p>
                </Grid>
              </Grid>
              <Grid container spacing={0.5} width={ (width - 100) } style={{ paddingTop: 10, paddingLeft: 20 }}>
                <Grid item size={6}>
                  <p style={{ margin: '7px 0px 0px 12px' }}>Selected Contributed Total: <strong>${commaFormatter(contributedCost)}</strong></p>
                </Grid>
                <Grid item size={6}>
                  <p style={{ margin: '7px 0px 0px 0px' }}>Selected Acres Total: <strong>{commaFormatter(totalAcres)}</strong></p>
                </Grid>
              </Grid>
              <Grid container spacing={0.5} width={ (width - 100) } style={{ paddingTop: 10, paddingLeft: 20 }}>
                <Grid item size={12}>
                  <p style={{ margin: '7px 0px 0px 12px' }}>Selected Funding Total: <strong>${commaFormatter(totalCost)}</strong></p>
                </Grid>
              </Grid>
            </Grid>
            <Grid item size={{sm: 12, md: 6}} style={{ marginBottom: 15}}>
              <Grid container spacing={0.5} width={ (width - 100) } style={{ paddingTop: 10, paddingLeft: 20 }}>
                <Grid item size={5}>
                  <Tooltip
                  title={
                    <span style={{ whiteSpace: 'pre-line' }}>{fullDefinitionExpression}</span>
                  } 
                  enterDelay={100}
                  >
                    <InfoIcon fontSize="medium" color="primary"/>
                  </Tooltip>
                  <h3 style={{margin: '7px 0 0 12px', display: 'inline'}}>All Filtered Result Totals</h3>
                </Grid>
              </Grid>
              <Grid container spacing={0.5} width={ (width - 100) } style={{ paddingTop: 10, paddingLeft: 20 }}>
                <Grid item size={6}>
                  <p style={{ margin: '7px 0px 0px 12px' }}>All BIL Funding Subtotal: <strong>${commaFormatter(allBilCost)}</strong></p>
                </Grid>
                <Grid item size={6}>
                  <p style={{ margin: '7px 0px 0px 0px' }}>All BIL Acres Subtotal: <strong>{commaFormatter(allBilAcres)}</strong></p>
                </Grid>
              </Grid>
              <Grid container spacing={0.5} width={ (width - 100) } style={{ paddingTop: 10, paddingLeft: 20 }}>
                <Grid item size={6}>
                  <p style={{ margin: '7px 0px 0px 12px' }}>All non-BIL Funding Subtotal: <strong>${commaFormatter(allNonBilCost)}</strong></p>
                </Grid>
                <Grid item size={6}>
                  <p style={{ margin: '7px 0px 0px 0px' }}>All non-BIL Acres Subtotal: <strong>{commaFormatter(allNonBilAcres)}</strong></p>
                </Grid>
              </Grid>
              <Grid container spacing={0.5} width={ (width - 100) } style={{ paddingTop: 10, paddingLeft: 20 }}>
                <Grid item size={6}>
                  <p style={{ margin: '7px 0px 0px 12px' }}>All Contributed Total: <strong>${commaFormatter(allContributedCost)}</strong></p>
                </Grid>
                <Grid item size={6}>
                  <p style={{ margin: '7px 0px 0px 0px' }}>All Acres Total: <strong>{commaFormatter(allTotalAcres)}</strong></p>
                </Grid>
              </Grid>
              <Grid container spacing={0.5} width={ (width - 100) } style={{ paddingTop: 10, paddingLeft: 20 }}>
                <Grid item size={12}>
                  <p style={{ margin: '7px 0px 0px 12px' }}>All Funding Total: <strong>${commaFormatter(allTotalCost)}</strong></p>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container rowSpacing={0.5} columnSpacing={1} >
            <Grid size={12} style={{ paddingTop: 20, paddingLeft: 20 }}>
              <ThemeProvider theme={theme}>
                <div style={{marginRight: 0, display: 'inline'}}>
                  <Checkbox className="approvalCheckboxes" style={{ borderColor: ColorConstants.BlueAlien }} id="localApprovalCheckbox" 
                      disabled={!isLocalApprover}
                      checked={isLocalDateChecked}
                      onChange={(event) => {
                        setIsLocalDateChecked(event.target.checked);
                      }} 
                  />
                  <label className="approvalCheckboxLabel" style={{ marginRight: 10 }}>Local Approval Date</label>
                </div>
              </ThemeProvider>
              <ThemeProvider theme={theme}>
                <div style={{marginRight: 0, display: 'inline'}}>
                  <Checkbox className="approvalCheckboxes" style={{ borderColor: ColorConstants.BlueAlien }} id="regionApprovalCheckbox" 
                      disabled={!isRegionApprover}
                      checked={isRegionDateChecked}
                      onChange={(event) => {
                        setIsRegionDateChecked(event.target.checked);
                      }} 
                  />
                  <label className="approvalCheckboxLabel">Regional Approval Date</label>
                </div>
              </ThemeProvider>
              <ThemeProvider theme={theme}>
                <div style={{marginRight: 0, display: 'inline'}}>
                  <Checkbox className="approvalCheckboxes" style={{ borderColor: ColorConstants.BlueAlien }} id="agencyApprovalCheckbox" 
                      disabled={!isAgencyApprover}
                      checked={isAgencyDateChecked}
                      onChange={(event) => {
                        setIsAgencyDateChecked(event.target.checked)
                      }} 
                  />
                  <label className="approvalCheckboxLabel" style={{ marginRight: 10 }}>Agency Approval Date</label>
                </div>
              </ThemeProvider>
              <ThemeProvider theme={theme}>
                <div style={{marginRight: 0, display: 'inline'}}>
                  <Checkbox className="approvalCheckboxes" style={{ borderColor: ColorConstants.BlueAlien }} id="departmentApprovalCheckbox" 
                      disabled={!isDepartmentApprover}
                      checked={isDepartmentDateChecked}
                      onChange={(event) => {
                        setIsDepartmentDateChecked(event.target.checked)
                      }} 
                  />
                  <label className="approvalCheckboxLabel">Department Approval Date</label>
                </div>
              </ThemeProvider>
            </Grid>
            <Grid size={12} style={{ paddingTop: 10, paddingLeft: 20 }}>
              <div style={{ marginTop: 0, verticalAlign: 'middle', height: 53, display: 'inline' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    format="MM/dd/yyyy"
                    id={`datePicker-approvalDate`}
                    disabled={(!isLocalDateChecked && !isRegionDateChecked && !isAgencyDateChecked && !isDepartmentDateChecked) || isGridLoading || selectionModel?.ids?.size === 0}
                    value={datePickerValue}
                    onChange={(value) => {
                      if(!isNaN(Date.parse(value))){
                        setDatePickerValue(value);
                      } else {
                        setDatePickerValue(null);
                      }
                    }}
                    slotProps={{ textField: { variant: 'outlined'}}}
                  />
                </LocalizationProvider>
              </div>
              <Button 
                variant="contained" 
                color="primary" 
                size="medium" 
                autoFocus 
                onClick={() => 
                {
                  if(!isApprovalDateInFuture)
                  {
                    confirmAssignApprovalDates();
                  }
                  setIsApprovalDateConfirmationDialogOpen(true)
                }} 
                style={{ marginLeft: 5, display: 'inline', height: 57, marginTop: 0, verticalAlign: 'middle', color: 'white', backgroundColor: (selectionModel?.ids?.size === 0 ? null : ColorConstants.BlueAlien)}}
                disabled={(!isLocalDateChecked && !isRegionDateChecked && !isAgencyDateChecked && !isDepartmentDateChecked) || isGridLoading || selectionModel?.ids?.size === 0}
              >
                {`Mark ${selectionModel?.ids?.size} Approved`}
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                size="medium" 
                autoFocus 
                onClick={() => changingApproval(false)} 
                style={{ marginLeft: 5, display: 'inline', height: 57, marginTop: 0, verticalAlign: 'middle', color: 'white', backgroundColor: (selectionModel?.ids?.size === 0 ? null : ColorConstants.GrayAlien)}}
                disabled={(!isLocalDateChecked && !isRegionDateChecked && !isAgencyDateChecked && !isDepartmentDateChecked) || isGridLoading || selectionModel?.ids?.size === 0}
              >
                {`Mark ${selectionModel?.ids?.size} UnApproved`}
              </Button>
              <Tooltip title={!selectedItemsCanUnapprove ? 'All selected items must have a Regional, Agency, or Department Approval Date' : ''}>
                <span>
                  <Button
                    variant="contained" 
                    color="primary" 
                    size="medium" 
                    autoFocus 
                    onClick={() => requestUnapproval(allSelectedItems)} 
                    style={{ marginLeft: 5, display: 'inline', height: 57, marginTop: 0, verticalAlign: 'middle'}}
                    disabled={!selectedItemsCanUnapprove || isGridLoading}
                  >
                    {`Request Unapproval: ${selectionModel?.ids?.size} Items`}
                  </Button>  
                </span>          
              </Tooltip>
            </Grid>
          </Grid>
          <div style={{ height: (height - 175) , width: width, display: 'flex', flexDirection: 'row', verticalAlign: 'center' }}>
            <DataGridPremium
              apiRef={apiRef}
              rows={gridValues}
              loading={isGridLoading}
              style={{ borderTop: 'none'}}
              getRowId={(row) => { 
                return `${row?.internalId}-${row?.OBJECTID}-${row?.GroupingID}`
              }}
              columns={gridColumns}
              rowsPerPageOptions={[10]}
              rowGroupingColumnMode="multiple"
              rowGroupingModel={rowGroupingModel}
              checkboxSelection
              disableRowSelectionOnClick
              initialState={initialState}
              defaultGroupingExpansionDepth={-1}
              isRowSelectable={handleIsRowSelectable}
              onRowGroupingModelChange={(model) => {
                setRowGroupingModel(model);
              }}
              onRowSelectionModelChange={(newSelection) => {
                let prevSelectionModel = [...new Set(selectionModel.ids)];
                let currSelectionModel = [...new Set(newSelection.ids)];
                let areRowsRemoved = prevSelectionModel.length > currSelectionModel.length;
                if(areRowsRemoved && currSelectionModel.length > 0)
                {
                  let removedItem = prevSelectionModel.filter(x => !currSelectionModel.includes(x));
                  let removedItemNode = apiRef.current.getRowNode(removedItem);
                  if(removedItemNode && removedItem && removedItem.length > 0 && !removedItemNode.groupingField)
                  {
                    let currentGridValues = gridValues.filter(x => (`${x.internalId}-${x.OBJECTID}-${x.GroupingID}` === removedItem[0]));
                    let gridValuesToRemove = gridValues.filter(x => (`${currentGridValues[0].OBJECTID}-${currentGridValues[0].Class}-${currentGridValues[0].GroupingID}` === `${x.OBJECTID}-${x.Class}-${x.GroupingID}`));
                    let gridValuesToRemoveRowIds = gridValuesToRemove.map(x => `${x.internalId}-${x.OBJECTID}-${x.GroupingID}`);
                    let selectionModelWithRelatedRemoved = currSelectionModel.filter(x => !gridValuesToRemoveRowIds.includes(x));
                    currSelectionModel = selectionModelWithRelatedRemoved;
                  }
                }

                let isGroupedByName = false;
                let firstItemNode = currSelectionModel.length > 0 ? apiRef.current.getRowNode(currSelectionModel[0]) : null;
                if(firstItemNode?.groupingField && firstItemNode.groupingField === "Name")
                {
                  isGroupedByName = true;
                }

                let selectedItems = [];
                let selectionCanUnapprove = true;
                currSelectionModel.forEach(item => {
                  const group = apiRef.current.getRowNode(item)
                  let selectedGridValues = [];
                  if(group.groupingField && group.groupingField === "Name"){
                      const itemSplit = item.split('/');
                      const allItemsAfterIdentifier = itemSplit.slice(1);
                      const rejoinedName = allItemsAfterIdentifier.join("/");
                      selectedGridValues = gridValues.filter(x => x["Name"] === rejoinedName);
                  } else {
                    let currentGridValues = gridValues.filter(x => (`${x.internalId}-${x.OBJECTID}-${x.GroupingID}` === item));
                    selectedGridValues = gridValues.filter(x => (`${currentGridValues[0].OBJECTID}-${currentGridValues[0].Class}-${currentGridValues[0].GroupingID}` === `${x.OBJECTID}-${x.Class}-${x.GroupingID}`));
                  }
                  if(selectedGridValues && selectedGridValues.length > 0){
                    selectedGridValues.forEach(gridVal => {
                      if(!gridVal[FieldNames.featureFields.regionalApprovalDate] && !gridVal[FieldNames.featureFields.agencyApprovalDate] && !gridVal[FieldNames.featureFields.departmentApprovalDate])
                      {
                        selectionCanUnapprove = false;
                      }
                      if(selectedItems.filter(x => (`${x.internalId}-${x.OBJECTID}-${x.GroupingID}`) === (`${gridVal.internalId}-${gridVal.OBJECTID}-${gridVal.GroupingID}`)).length === 0)
                      {
                        selectedItems.push(gridVal);
                      }
                    });
                  }
                })
                
                if(isGroupedByName || currSelectionModel.length === 0)
                {
                  setSelectionModel({ids: new Set(currSelectionModel), type: 'include'});
                }
                else
                {
                  // Set selection model after we have obtained related rows for ungrouped items
                  let selectedItemsRowIds = selectedItems.map(x => `${x.internalId}-${x.OBJECTID}-${x.GroupingID}`);
                  let selectedUniqueRowIds = new Set(selectedItemsRowIds);
                  setSelectionModel({ids: selectedUniqueRowIds, type: 'include'});
                }

                // Approval Logic
                let isRequestUnapproveAllowed = selectionCanUnapprove && selectedItems && selectedItems.length > 0;

                // Filtered Items
                const selectedBilItems = selectedItems.filter(si => 
                  (si[FieldNames.fundingFields.fundingSource] && 
                  si[FieldNames.fundingFields.fundingSource].includes(FieldNames.contributedCheck.bil) && 
                  !si[FieldNames.fundingFields.fundingSource].includes(FieldNames.contributedCheck.contributed)));
                const selectedNonBilItems = selectedItems.filter(si =>
                  (si[FieldNames.fundingFields.fundingSource] && 
                  !si[FieldNames.fundingFields.fundingSource].includes(FieldNames.contributedCheck.bil) &&
                  !si[FieldNames.fundingFields.fundingSource].includes(FieldNames.contributedCheck.contributed)));
                const selectedContributedItems = selectedItems.filter(si => si[FieldNames.fundingFields.fundingSource]?.includes(FieldNames.contributedCheck.contributed))
                // Cost Calculations
                const newBilCost = selectedBilItems.map(item => item[FieldNames.fundingFields.totalCost]).reduce((a, b) => a + b, 0);
                const newNonBilCost = selectedNonBilItems.map(item => item[FieldNames.fundingFields.totalCost]).reduce((a, b) => a + b, 0);
                const newContributedCost = selectedContributedItems.map(item => item[FieldNames.fundingFields.totalCost]).reduce((a, b) => a + b, 0);
                const newTotalCost = selectedItems.map(item => item[FieldNames.fundingFields.totalCost]).reduce((a, b) => a + b, 0);
                // Acre Calculations
                const newBilCalculatedArea = selectedBilItems.map(item => item[FieldNames.featureFields.calculatedAcresFundingSingleLineView]).reduce((a, b) => a + b, 0);
                const newBilCalArea = Math.round(newBilCalculatedArea * 100) / 100;
                const newContributedCalculatedArea = selectedContributedItems.map(item => item[FieldNames.featureFields.calculatedAcresFundingSingleLineView]).reduce((a, b) => a + b, 0);
                const newNonBilCalculatedArea = selectedNonBilItems.map(item => item[FieldNames.featureFields.calculatedAcresFundingSingleLineView]).reduce((a, b) => a + b, 0);
                const newNonBilCalArea = Math.round((newNonBilCalculatedArea + newContributedCalculatedArea) * 100) / 100
                const newCalculatedArea = selectedItems.map(item => item[FieldNames.featureFields.calculatedAcresFundingSingleLineView]).reduce((a, b) => a + b, 0);
                const newCalArea = Math.round(newCalculatedArea * 100) / 100
                //Do we need to round this?
                setBilCost(newBilCost);
                setNonBilCost(newNonBilCost);
                setContributedCost(newContributedCost);
                setTotalCost(newTotalCost);
                setBilAcres(newBilCalArea);
                setNonBilAcres(newNonBilCalArea);
                setTotalAcres(newCalArea);
                setSelectedItemsCanUnapprove(isRequestUnapproveAllowed);
                setAllSelectedItems(selectedItems);
              }}
              classes={classes}
              groupingColDef={(params) => {
                return { hideDescendantCount: true }
              }}
              rowSelectionModel={selectionModel}
              slots={{
                toolbar: CustomExportToolbar,
              }}
              showToolbar
            />
          </div>
          <ThemeProvider theme={theme}>
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={6000}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              className={classes.snackBarStyle}
              onClose={() => {
                setSnackbarOpen(false);
                setSnackbarMessage("");
              }}
            >
              <Alert
                onClose={() => {
                  setSnackbarOpen(false);
                  setSnackbarMessage("");
                }}
                severity={snackbarSeverity}
                sx={{ width: "100%" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
          </ThemeProvider>
        </Box>
      </Paper>
    </Box>
  );
}
